import { Component, Input, OnChanges, OnInit } from '@angular/core';
import {
  CommentsBody,
  CommentsResponse,
} from '@modules/search/interfaces/interface';
import {
  EditorComponent,
  EditorOutput,
} from './components/editor/editor.component';
import { AuthService } from '@core/services/auth/auth.service';
import { UserResponse } from '@shared/interfaces/users.interface';
import { ButtonModule } from 'primeng/button';
import { SearchService } from '@modules/search/services/search.service';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { Notification } from '@modules/notification/interface/notification.interface';
import { NotificationService } from '@modules/notification/service/notification.service';
import { CommonModule } from '@angular/common';
import { SanitizerPipe } from '@shared/pipes/sanitizer.pipe';
import { TimeAgoPipe } from '@shared/pipes/time-ago.pipe';
import { ThemeService } from '@shared/services/theme/theme.service';
import { MenuModule } from 'primeng/menu';
import { ConfirmPopupModule } from 'primeng/confirmpopup';

@Component({
  selector: 'app-comments',
  standalone: true,
  imports: [
    EditorComponent,
    ButtonModule,
    CommonModule,
    SanitizerPipe,
    TimeAgoPipe,
    MenuModule,
    ConfirmPopupModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './comments.component.html',
  styleUrl: './comments.component.scss',
})
export class CommentsComponent implements OnChanges, OnInit {
  @Input() data: any;
  items: MenuItem[] | undefined;
  body: string = '';
  comments: CommentsResponse[] = [];
  usersList: UserResponse[] = [];
  usersNameAndEmail: { value: string; id: string }[] = [];
  mentionUsers: string[] = [];
  sortBy: string = 'desc';

  constructor(
    protected _auth: AuthService,
    private _search: SearchService,
    private messageService: MessageService,
    private _notification: NotificationService,
    protected _theme: ThemeService,
    private _confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.getUsersList();
  }

  ngOnChanges() {
    if (this.data && this.data._source && this.data._source.comments) {
      this.comments = this.data._source.comments;
    } else {
      this.data._source.comments = [];
      this.comments = this.data._source.comments;
    }
  }

  getBackground(email: string) {
    let profileTextColor = this._theme.getColorOnName(email);
    let profileColors = this._theme.getBackgroundColor(profileTextColor);
    return profileColors.backgroundColor;
  }

  getTextColor(email: string) {
    let profileTextColor = this._theme.getColorOnName(email);
    let profileColors = this._theme.getBackgroundColor(profileTextColor);
    return profileColors.color;
  }

  getUsersList() {
    this.usersList = this._auth.userList();
    this.usersNameAndEmail = this.usersList.map((user: UserResponse) => {
      return { value: user.firstName + ' ' + user.lastName, id: user.email };
    });
  }

  isSubmitting: boolean = false;
  submitComment = () => {
    let commentBody: CommentsBody = {
      body: this.body,
      id: this.data._id,
      index: this.data._index,
      operation: 'add',
      taggedUsers: this.mentionUsers,
    };
    this.isSubmitting = true;
    this._search.addComment(commentBody).subscribe({
      next: (res: any) => {
        if (this.mentionUsers.length === 0) {
          this.messageService.add({
            severity: 'success',
            summary: 'Comment added successfully',
            detail: 'It may take a few minutes to reflect',
            key: 'tr',
          });
          this.isSubmitting = false;
          this.mentionUsers = [];
          this.body = '';
          if (this.data && this.data._source && this.data._source.comments) {
            this.data._source.comments.push(res.comment);
          } else if (this.data && this.data._source) {
            this.data._source.comments = [res.comment];
          }
        } else {
          let notificationBody: Notification = {
            esId: this.data._id,
            esIndex: this.data._index,
            recipients: this.mentionUsers,
            context: 'comment',
            scheduledDate: new Date(),
            message: this.body,
          };
          this._notification.setNotification(notificationBody).subscribe({
            next: (response) => {
              this.isSubmitting = false;
              this.mentionUsers = [];
              this.body = '';
              if (
                this.data &&
                this.data._source &&
                this.data._source.comments
              ) {
                this.data._source.comments.push(res.comment);
              } else if (this.data && this.data._source) {
                this.data._source.comments = [res.comment];
              }
              this.messageService.add({
                severity: 'success',
                summary: 'Comment added successfully',
                detail: 'It may take a few minutes to reflect',
                key: 'tr',
              });
            },
            error: (error) => {
              this.messageService.add({
                severity: 'warn',
                key: 'tr',
                summary: 'Something went wrong',
                detail: 'Error whiles setting notification',
              });
              console.log(error);
            },
          });
        }
      },
      error: (err: any) => {
        console.log(err);
        this.messageService.add({
          severity: 'warn',
          summary: 'Something went wrong',
          detail: 'Failed to add comment',
          key: 'tr',
        });
      },
    });
  };

  isDeleting: boolean = false;
  deletingIndex: number = -1;
  deleteComment = (event: Event, comment: CommentsResponse, index: number) => {
    if (this._auth.userDetail().email !== comment.createdBy) {
      this.messageService.add({
        severity: 'warn',
        summary: 'You are not authorized',
        detail: 'Only the creator of the comment can delete it',
        key: 'tr',
      });
      return;
    }

    let commentBody: CommentsBody = {
      id: this.data._id,
      index: this.data._index,
      operation: 'remove',
      uuid: comment.uuid,
    };
    this._confirmationService.confirm({
      header: 'Delete comment',
      target: event.target as EventTarget,
      icon: 'pi pi-exclamation-triangle',
      message: 'Are you sure you want to delete this comment?',
      acceptIcon: 'pi pi-check mr-1',
      rejectIcon: 'pi pi-times mr-1',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      rejectButtonStyleClass:
        'p-button-sm p-button-text p-button-text p-button-plain',
      acceptButtonStyleClass: 'p-button-sm bg-red-600',
      accept: () => {
        this.isDeleting = true;
        this.deletingIndex = index;
        this._search.addComment(commentBody).subscribe({
          next: (res: any) => {
            this.isDeleting = false;
            this.comments.splice(index, 1);
            this.deletingIndex = -1;
            this.data._source.comments = this.comments;
            this.messageService.add({
              severity: 'success',
              summary: 'Comment deleted successfully',
              detail: 'It may take a few minutes to reflect',
              key: 'tr',
            });
          },
          error: (err: any) => {
            console.log(err);
            this.isDeleting = false;
            this.deletingIndex = -1;
            this.messageService.add({
              severity: 'warn',
              summary: 'Something went wrong',
              detail: 'Failed to delete comment',
              key: 'tr',
            });
          },
        });
      },
    });
  };

  onBodyChange = (data: EditorOutput) => {
    this.body = data.body;
    this.mentionUsers = data.mentionedUsers;
  };
}
